/**
 * QuestionCounterLabel — reusable numbered badge rendered before each question item.
 *
 * Displays a compact, rounded badge (e.g. "1", "2", "3") to the left of a question
 * so users can easily identify question numbers across all content types.
 *
 * Usage:
 * ```tsx
 * <QuestionCounterLabel index={0} /> // renders "1"
 * <QuestionCounterLabel index={2} label="Q" /> // renders "Q3"
 * ```
 */
import * as React from 'react';
import { Badge } from '@ai-content-hub/content-kit';
import { cn } from '../lib/utils';

export interface QuestionCounterLabelProps {
    /** 0-based index of the question item. Displayed as `index + 1`. */
    index: number;
    /** Optional prefix label (e.g. "Q", "Ex"). If omitted, only the number is shown. */
    label?: string;
    /** Additional CSS class names. */
    className?: string;
}

/**
 * Compact numbered badge for question items.
 *
 * Renders inline with the badge styling used by question-set's `elementChildLabels`
 * (rounded-md, muted background, bold text) so the look is consistent.
 */
export const QuestionCounterLabel: React.FC<QuestionCounterLabelProps> = ({
    index,
    label,
    className,
}) => {
    const display = label ? `${label}${index + 1}` : String(index + 1);
    return (
        <Badge
            variant="ghost"
            className={cn(
                'ach-question-counter-label',
                'h-7 min-w-7 rounded-md border-transparent bg-muted-foreground/15 text-sm font-bold text-foreground select-none',
                className
            )}
            aria-label={`Question ${index + 1}`}
        >
            {display}
        </Badge>
    );
};

export default QuestionCounterLabel;
